/**
 * Firestore database-operasjoner
 */

import { 
  collection, 
  doc, 
  setDoc, 
  updateDoc,
  deleteDoc, 
  addDoc,
  arrayUnion,
  arrayRemove,
  getDocs
} from 'firebase/firestore';
import { db } from './firebase';
import { APP_ID } from '../constants/config';
import { 
  validateUsername, 
  validateActivity,
  validateCompetitionTitle,
  validateDescription,
  validateGoal,
  validateCompetitionMode,
  validateChatMessage
} from '../utils/validation';

/**
 * Hent collection path
 */
const getCollectionPath = (...paths) => {
  return ['artifacts', APP_ID, ...paths];
};

/**
 * Oppdater brukerprofil
 */
export const updateUserProfile = async (userId, profileData) => {
  // Valider navn hvis det finnes
  if (profileData.name) {
    profileData.name = validateUsername(profileData.name);
  }
  
  // Sikre at userId er en string
  if (!userId || typeof userId !== 'string') {
    throw new Error('Ugyldig bruker-ID');
  }
  
  const profileRef = doc(db, ...getCollectionPath('users', userId, 'profile', 'settings'));
  // Bruk merge: true for å ikke overskrive eksisterende felt
  await setDoc(profileRef, profileData, { merge: true });
};

/**
 * Legg til aktivitet
 */
export const addActivity = async (userId, activityData) => {
  // Valider bruker-ID
  if (!userId || typeof userId !== 'string') {
    throw new Error('Ugyldig bruker-ID');
  }
  
  // Valider aktivitetsdata
  const validatedActivity = validateActivity(activityData);
  
  const activitiesRef = collection(db, ...getCollectionPath('users', userId, 'activities'));
  return await addDoc(activitiesRef, validatedActivity);
};

/**
 * Slett aktivitet
 */
export const deleteActivity = async (userId, activityId) => {
  const activityRef = doc(db, ...getCollectionPath('users', userId, 'activities', activityId));
  await deleteDoc(activityRef);
};

/**
 * Oppdater leaderboard
 */
export const updateLeaderboard = async (userId, leaderboardData) => {
  // Valider bruker-ID
  if (!userId || typeof userId !== 'string') {
    throw new Error('Ugyldig bruker-ID');
  }
  
  // Valider navn
  if (leaderboardData.name) {
    leaderboardData.name = validateUsername(leaderboardData.name);
  }
  
  // Sikre at totalKm er et tall
  if (leaderboardData.totalKm !== undefined) {
    const km = Number(leaderboardData.totalKm);
    if (isNaN(km) || km < 0) {
      throw new Error('Ugyldig totalKm');
    }
    leaderboardData.totalKm = km;
  }
  
  const leaderboardRef = doc(db, ...getCollectionPath('public', 'data', 'leaderboard', userId));
  await setDoc(leaderboardRef, leaderboardData, { merge: true });
};

/**
 * Opprett konkurranse
 */
export const createCompetition = async (competitionData) => {
  // Valider alle felt
  const validated = {
    title: validateCompetitionTitle(competitionData.title),
    description: validateDescription(competitionData.description || ''),
    mode: validateCompetitionMode(competitionData.mode),
    goal: validateGoal(competitionData.goal),
    code: competitionData.code, // Allerede validert i formatters.js
    creatorId: String(competitionData.creatorId),
    participants: Array.isArray(competitionData.participants) ? competitionData.participants : [],
    teams: competitionData.teams || {},
    createdAt: competitionData.createdAt || new Date().toISOString(),
    startDate: competitionData.startDate || null,
    endDate: competitionData.endDate || null,
    seenBy: competitionData.seenBy || {}
  };
  
  // Valider creatorId
  if (!validated.creatorId || validated.creatorId.length === 0) {
    throw new Error('Creator ID er påkrevd');
  }
  
  const competitionsRef = collection(db, ...getCollectionPath('public', 'data', 'competitions'));
  return await addDoc(competitionsRef, validated);
};

/**
 * Bli med i konkurranse
 */
export const joinCompetition = async (competitionId, userId, competition = null) => {
  const competitionRef = doc(db, ...getCollectionPath('public', 'data', 'competitions', competitionId));
  
  const updateData = { 
    participants: arrayUnion(userId) 
  };
  
  // Auto-assign team for tug_of_war competitions
  if (competition?.mode === 'tug_of_war') {
    const teams = competition.teams || {};
    const teamA = Object.values(teams).filter(t => t === 'A').length;
    const teamB = Object.values(teams).filter(t => t === 'B').length;
    const assignedTeam = teamA <= teamB ? 'A' : 'B';
    updateData[`teams.${userId}`] = assignedTeam;
  }
  
  await updateDoc(competitionRef, updateData);
  
  // Returner true for å signalisere at brukeren bør synkronisere
  return true;
};

/**
 * Forlat konkurranse
 */
export const leaveCompetition = async (competitionId, userId) => {
  const competitionRef = doc(db, ...getCollectionPath('public', 'data', 'competitions', competitionId));
  
  await updateDoc(competitionRef, { 
    participants: arrayRemove(userId) 
  });
};

/**
 * Send chat-melding
 */
export const sendChatMessage = async (competitionId, messageData) => {
  // Valider competition ID
  if (!competitionId || typeof competitionId !== 'string') {
    throw new Error('Ugyldig konkuranse-ID');
  }
  
  // Valider melding
  const validated = {
    text: validateChatMessage(messageData.text),
    senderId: String(messageData.senderId),
    senderName: validateUsername(messageData.senderName),
    avatar: String(messageData.avatar || '🏃'),
    timestamp: messageData.timestamp || new Date().toISOString()
  };
  
  // Valider senderId
  if (!validated.senderId || validated.senderId.length === 0) {
    throw new Error('Sender ID er påkrevd');
  }
  
  const chatRef = collection(db, ...getCollectionPath('public', 'data', 'competitions', competitionId, 'chat'));
  return await addDoc(chatRef, validated);
};

/**
 * Slett chat-melding
 */
export const deleteChatMessage = async (competitionId, messageId) => {
  const messageRef = doc(db, ...getCollectionPath('public', 'data', 'competitions', competitionId, 'chat', messageId));
  await deleteDoc(messageRef);
};

/**
 * Marker konkurranse som sett av bruker
 */
export const markCompetitionAsSeen = async (competitionId, userId) => {
  const competitionRef = doc(db, ...getCollectionPath('public', 'data', 'competitions', competitionId));
  await updateDoc(competitionRef, {
    [`seenBy.${userId}`]: new Date().toISOString()
  });
};

/**
 * Slett konkurranse
 */
export const deleteCompetition = async (competitionId) => {
  const competitionRef = doc(db, ...getCollectionPath('public', 'data', 'competitions', competitionId));
  await deleteDoc(competitionRef);
};

/**
 * Slett all brukerdata
 */
export const deleteAllUserData = async (userId) => {
  try {
    console.log('Starting deleteAllUserData for userId:', userId);
    
    // Slett alle aktiviteter
    const activitiesRef = collection(db, ...getCollectionPath('users', userId, 'activities'));
    const activitiesSnapshot = await getDocs(activitiesRef);
    console.log(`Deleting ${activitiesSnapshot.docs.length} activities`);
    const deleteActivitiesPromises = activitiesSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteActivitiesPromises);

    // Slett brukerprofil
    console.log('Deleting user profile');
    const profileRef = doc(db, ...getCollectionPath('users', userId, 'profile', 'settings'));
    await deleteDoc(profileRef);

    // Slett leaderboard-oppføring
    console.log('Deleting leaderboard entry');
    const leaderboardRef = doc(db, ...getCollectionPath('public', 'data', 'leaderboard', userId));
    await deleteDoc(leaderboardRef);

    // Hent alle konkurranser
    console.log('Fetching all competitions');
    const competitionsRef = collection(db, ...getCollectionPath('public', 'data', 'competitions'));
    const competitionsSnapshot = await getDocs(competitionsRef);
    console.log(`Found ${competitionsSnapshot.docs.length} total competitions`);
    
    // Slett konkurranser opprettet av brukeren
    const competitionsToDelete = competitionsSnapshot.docs.filter(doc => doc.data().creatorId === userId);
    console.log(`Deleting ${competitionsToDelete.length} competitions created by user`);
    competitionsToDelete.forEach(doc => {
      console.log(`  - Deleting competition: ${doc.data().title} (${doc.id})`);
    });
    const deleteCompetitionsPromises = competitionsToDelete.map(doc => deleteDoc(doc.ref));
    await Promise.all(deleteCompetitionsPromises);

    // Fjern bruker fra konkurranser de deltar i (men ikke opprettet)
    const competitionsToLeave = competitionsSnapshot.docs.filter(
      doc => doc.data().participants?.includes(userId) && doc.data().creatorId !== userId
    );
    console.log(`Leaving ${competitionsToLeave.length} competitions`);
    const leaveCompetitionsPromises = competitionsToLeave.map(doc => 
      updateDoc(doc.ref, { participants: arrayRemove(userId) })
    );
    await Promise.all(leaveCompetitionsPromises);

    console.log('All user data deleted successfully');
  } catch (error) {
    console.error('Error deleting user data:', error);
    throw error;
  }
};

/**
 * Hent collection references for listeners
 */
export const getLeaderboardCollection = () => 
  collection(db, ...getCollectionPath('public', 'data', 'leaderboard'));

export const getCompetitionsCollection = () => 
  collection(db, ...getCollectionPath('public', 'data', 'competitions'));

export const getUserProfileDoc = (userId) => 
  doc(db, ...getCollectionPath('users', userId, 'profile', 'settings'));

export const getUserActivitiesCollection = (userId) => 
  collection(db, ...getCollectionPath('users', userId, 'activities'));

export const getChatCollection = (competitionId) => 
  collection(db, ...getCollectionPath('public', 'data', 'competitions', competitionId, 'chat'));
